import axios from 'axios';
import {IAmbulance} from 'app/shared/model/ambulance.model';
import {IAmbulanceCompact} from 'app/shared/model/ambulance-compact.model';

/**
 * Ambulance Service
 * Contains synchronous API calls for ambulance search functionality
 */
class AmbulanceService {
  private apiUrl = 'api/ambulances';

  /**
   * Get ambulance by ID
   */
  async getAmbulance(id: string): Promise<IAmbulance> {
    const requestUrl = `${this.apiUrl}/${id}/with-facility`;
    const response = await axios.get<IAmbulance>(`${requestUrl}?cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get ambulance drivers for facility and vehicle required-level
   */
  async getAmbulanceDriversForFacilityVehicleRequiredLevel(healthcareFacilityId: string, requiredLevel: number): Promise<IAmbulance[]> {
    if (!healthcareFacilityId || !requiredLevel) {
      return [];
    }
    const requestUrl = `api/ambulance-by-facility-and-vehicle-level?healthcareFacilityId=${healthcareFacilityId}&requiredLevel=${requiredLevel}`;
    const response = await axios.get<IAmbulance[]>(`${requestUrl}&cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get ambulance drivers for neighbor facility and vehicle required-level
   */
  async getAmbulanceDriversForNeighborFacilityVehicleRequiredLevel(healthcareFacilityId: string, requiredLevel: number): Promise<IAmbulance[]> {
    if (!healthcareFacilityId || !requiredLevel) {
      return [];
    }
    const requestUrl = `api/ambulance-by-neighbor-facility-and-vehicle-level?healthcareFacilityId=${healthcareFacilityId}&requiredLevel=${requiredLevel}`;
    const response = await axios.get<IAmbulance[]>(`${requestUrl}&cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Search ambulance by name
   */
  async getAmbulanceByName(searchString: string, countryId: string): Promise<IAmbulance[]> {
    const requestUrl = `api/search/search-ambulance-by-name/${searchString}/${countryId}`;
    const response = await axios.get<IAmbulance[]>(`${requestUrl}?cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get all ambulances for a country
   */
  async getAmbulances(page?: number, size?: number, sort?: string, countryId?: string): Promise<IAmbulance[]> {
    const requestUrl = `${this.apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&countryId=${countryId}` : ''}`;
    const response = await axios.get<IAmbulance[]>(`${requestUrl}${sort ? '&' : '?'}cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get all ambulances in compact format for a country
   */
  async getAmbulancesCompact(countryId?: string): Promise<IAmbulanceCompact[]> {
    const requestUrl = `${this.apiUrl}/compact`;
    const params = new URLSearchParams();
    if (countryId) params.append('countryId', countryId);
    const response = await axios.get<IAmbulanceCompact[]>(`${requestUrl}?${params.toString()}`);
    return response.data;
  }
}

// Export singleton instance
export const ambulanceService = new AmbulanceService();
export default ambulanceService;
